import { useCallback, useEffect, useRef, useState } from 'react';
import type React from 'react';

import { useSyncRef } from '@/lib/hooks';

import { usePriceConfig } from './usePriceConfig';

const FLASH_DURATION_MS = 300;

interface UsePriceTrackingResult {
    currentValue: number;
    targetValue: number;
    /**
     * The market `currentValue` actually belongs to, or null while it belongs to
     * no market yet.
     *
     * `currentValue` is a spring, and switching markets used to leave it
     * animating from the old market's price to the new one — sweeping through
     * every value between $78,467 and $0.1406 on the way. Those intermediate
     * numbers belong to neither market, and anything that applied them to the
     * new market's position produced nonsense: a 5x LONG on a $0.14 coin briefly
     * read -$27,855,852.53 (-279,189,353.66%), because its PnL anchor had been
     * stamped at the previous market's price.
     *
     * Consumers that price a position off this MUST check the tag first. It is
     * cheaper and far more robust than trying to guarantee the spring has settled.
     */
    currentValueCoin: string | null;
    priceFlash: 'up' | 'down' | null;
    handleCurrentValueChange: (value: number, priceDiff: number) => void;
    currentValueRef: React.RefObject<number>;
}

export function usePriceTracking(
    feedPrice: number | null,
    /** the market `feedPrice` is for; a change resets rather than springs. */
    coin: string | null = null,
): UsePriceTrackingResult {
    const [currentValue, setCurrentValue] = useState<number>(100);
    const [targetValue, setTargetValue] = useState<number>(100);
    const [priceFlash, setPriceFlash] = useState<'up' | 'down' | null>(null);
    const flashTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const currentValueRef = useSyncRef(currentValue);
    // null until the first feed price for `coin` lands, so the stale price left
    // over from the previous market is never mistaken for this one's.
    const [currentValueCoin, setCurrentValueCoin] = useState<string | null>(
        null,
    );
    const coinRef = useRef(coin);

    // Use targetValue (WebSocket updates at ~10Hz) instead of currentValue
    // (animation at 60fps) to avoid unnecessary hook evaluations
    const priceConfig = usePriceConfig(targetValue);

    // A market change untags the price and drops the spring's history. Without
    // this the chart animates BETWEEN two unrelated markets — a meaningless
    // sweep to draw, and a meaningless number to price a position against.
    useEffect(() => {
        coinRef.current = coin;
        setCurrentValueCoin(null);
    }, [coin]);

    useEffect(() => {
        if (feedPrice === null) return;
        setTargetValue(feedPrice);
        setCurrentValueCoin((prev) => {
            if (prev === coinRef.current) return prev;
            // first price for this market: snap the spring to it instead of
            // travelling there from the previous market's scale.
            setCurrentValue(feedPrice);
            return coinRef.current;
        });
    }, [feedPrice]);

    const handleCurrentValueChange = useCallback(
        (value: number, priceDiff: number) => {
            setCurrentValue(value);

            if (Math.abs(priceDiff) > priceConfig.flashThreshold) {
                setPriceFlash(priceDiff > 0 ? 'up' : 'down');

                if (flashTimeoutRef.current) {
                    clearTimeout(flashTimeoutRef.current);
                }
                flashTimeoutRef.current = setTimeout(
                    () => setPriceFlash(null),
                    FLASH_DURATION_MS,
                );
            }
        },
        [priceConfig.flashThreshold],
    );

    useEffect(() => {
        return () => {
            if (flashTimeoutRef.current) {
                clearTimeout(flashTimeoutRef.current);
            }
        };
    }, []);

    return {
        currentValue,
        targetValue,
        currentValueCoin,
        priceFlash,
        handleCurrentValueChange,
        currentValueRef,
    };
}
